import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, AlertTriangle, Lightbulb, Target, Wrench, Heart } from 'lucide-react';

const journeySteps = [
  {
    year: '2017',
    title: 'My Beginning',
    icon: TrendingUp,
    color: 'tech-blue',
    content: 'I started investing at 27 with zero knowledge. Just ₹3,000/month into Axis ELSS and a few other Mutual Funds. Kept doing the same thing for THREE years — 2017, 2018, 2019 — same ₹3K, same fund, zero tracking.',
    highlight: '₹3K/month',
    highlightLabel: 'First SIP',
    side: 'left'
  },
  {
    year: '2020-21',
    title: 'The Wake-Up Call',
    icon: AlertTriangle,
    color: 'missed-gold',
    content: 'COVID happened. Markets crashed. Even for my small 3L portfolio, I panicked. Then I started actually reading about investing, goal planning. Created a diversified portfolio: 50% Large Cap, 25% Mid Cap, 25% Small Cap. Gradually increased SIPs from ₹30K to ₹1L/month.',
    highlight: '₹30K → ₹1L',
    highlightLabel: 'SIP Evolution',
    side: 'right'
  },
  {
    year: '2026',
    title: 'The False Confidence',
    icon: Lightbulb,
    color: 'wealth-green',
    content: 'Total invested: ₹53L. Current value: ₹63L. Gains: ₹9.75L (18% absolute return). XIRR: 10.71%. Portfolio looking green, daily movement exceeding my initial monthly SIP. I felt like I finally figured this out.',
    highlight: '₹63L',
    highlightLabel: 'Portfolio Value',
    side: 'left'
  },
  {
    year: 'THE PROBLEM',
    title: 'What Bugged Me',
    icon: Target,
    color: 'alert-red',
    content: 'Traditional comparison methods — rolling returns, point-to-point returns — they show average performance. But everyone invests at different times and exits at different times. A 20% return for one person is NOT the same for another in the same fund. I realized: generic comparisons will NOT tell me the real picture of MY investments.',
    highlight: 'Generic ≠ Real',
    highlightLabel: 'The Insight',
    side: 'right',
    isQuestion: true
  },
  {
    year: 'THE SOLUTION',
    title: 'What I Built',
    icon: Wrench,
    color: 'tech-blue',
    content: 'I spent countless nights building something in Python and Power BI to simulate every SIP, every transaction date, every amount — mimicked exactly on benchmark funds. If I put the EXACT money on the EXACT dates into Nifty 50 or Gold, what would have happened? This tool gave me answers I never expected.',
    highlight: 'Portfolio Auditor',
    highlightLabel: 'Was Born',
    side: 'left'
  },
  {
    year: 'TODAY',
    title: 'Sharing It With You',
    icon: Heart,
    color: 'wealth-green',
    content: 'This tool helped me identify underperforming funds, optimize my asset allocation, and make data-driven decisions. Now I\'m sharing it with fellow investors who want to know the REAL performance of their portfolios — not assumptions, not averages, but YOUR actual returns vs what could have been.',
    highlight: 'Your Turn',
    highlightLabel: 'Get Started',
    side: 'right'
  }
];

const JourneyTimeline = () => {
  return (
    <section id="journey-section" className="relative py-24 lg:py-32 bg-deep-space-secondary overflow-hidden" data-testid="journey-timeline">
      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:32px_32px]" />
      
      <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
        {/* Section Header - Personal Touch */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 bg-tech-blue/10 border border-tech-blue/20 rounded-full px-4 py-2 mb-6">
            <span className="text-sm text-tech-blue font-ibm-plex">Founder's Story</span>
          </div>
          <h2 className="font-outfit text-3xl lg:text-5xl font-bold text-white tracking-tight">
            Why I Built Portfolio Auditor
          </h2>
          <p className="mt-4 text-lg text-slate-400 font-ibm-plex max-w-2xl mx-auto">
            This isn't just another portfolio tracker. It's born from my own investing journey, 
            my own mistakes, and my search for real answers.
          </p>
        </motion.div>
        
        {/* Timeline */}
        <div className="relative">
          {/* Center Line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-tech-blue via-missed-gold to-wealth-green hidden lg:block" />
          
          {/* Timeline Steps */}
          <div className="space-y-12 lg:space-y-24">
            {journeySteps.map((step, index) => {
              const Icon = step.icon;
              const isLeft = step.side === 'left';
              
              return (
                <motion.div
                  key={step.year}
                  initial={{ opacity: 0, x: isLeft ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`relative flex flex-col lg:flex-row items-center gap-8 ${isLeft ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}
                >
                  {/* Content Card */}
                  <div className={`flex-1 ${isLeft ? 'lg:text-right' : 'lg:text-left'}`}>
                    <div className={`
                      bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 lg:p-8
                      shadow-[0_8px_32px_rgba(0,0,0,0.2)]
                      ${step.isQuestion ? 'border-alert-red/30 bg-alert-red/5' : ''}
                    `}>
                      {/* Year Badge */}
                      <div className={`inline-flex items-center gap-2 rounded-full px-4 py-1 mb-4`}
                        style={{ 
                          backgroundColor: step.color === 'tech-blue' ? 'rgba(59,130,246,0.1)' :
                                          step.color === 'missed-gold' ? 'rgba(255,215,0,0.1)' :
                                          step.color === 'wealth-green' ? 'rgba(16,185,129,0.1)' :
                                          'rgba(255,59,48,0.1)',
                          borderWidth: '1px',
                          borderStyle: 'solid',
                          borderColor: step.color === 'tech-blue' ? 'rgba(59,130,246,0.2)' :
                                       step.color === 'missed-gold' ? 'rgba(255,215,0,0.2)' :
                                       step.color === 'wealth-green' ? 'rgba(16,185,129,0.2)' :
                                       'rgba(255,59,48,0.2)'
                        }}
                      >
                        <span className={`font-jetbrains text-sm font-bold ${
                          step.color === 'tech-blue' ? 'text-tech-blue' :
                          step.color === 'missed-gold' ? 'text-missed-gold' :
                          step.color === 'wealth-green' ? 'text-wealth-green' :
                          'text-alert-red'
                        }`}>{step.year}</span>
                      </div>
                      
                      <h3 className="font-outfit text-xl lg:text-2xl font-bold text-white mb-3">
                        {step.title}
                      </h3>
                      
                      <p className={`text-slate-300 font-ibm-plex leading-relaxed ${step.isQuestion ? 'text-lg' : ''}`}>
                        {step.content}
                      </p>
                      
                      {/* Highlight Stat */}
                      <div className="mt-6 pt-4 border-t border-white/10">
                        <div className={`font-jetbrains text-2xl font-bold ${
                          step.color === 'tech-blue' ? 'text-tech-blue' :
                          step.color === 'missed-gold' ? 'text-missed-gold' :
                          step.color === 'wealth-green' ? 'text-wealth-green' :
                          'text-alert-red'
                        }`}>
                          {step.highlight}
                        </div>
                        <div className="text-sm text-slate-500 font-ibm-plex">{step.highlightLabel}</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Center Icon */}
                  <div className="relative z-10 flex-shrink-0">
                    <div className={`
                      w-16 h-16 rounded-full flex items-center justify-center
                      border-2 ${
                        step.color === 'tech-blue' ? 'border-tech-blue bg-tech-blue/20' :
                        step.color === 'missed-gold' ? 'border-missed-gold bg-missed-gold/20' :
                        step.color === 'wealth-green' ? 'border-wealth-green bg-wealth-green/20' :
                        'border-alert-red bg-alert-red/20'
                      }
                    `}>
                      <Icon className={`w-7 h-7 ${
                        step.color === 'tech-blue' ? 'text-tech-blue' :
                        step.color === 'missed-gold' ? 'text-missed-gold' :
                        step.color === 'wealth-green' ? 'text-wealth-green' :
                        'text-alert-red'
                      }`} />
                    </div>
                  </div>
                  
                  {/* Spacer for alignment */}
                  <div className="flex-1 hidden lg:block" />
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default JourneyTimeline;
